import React from 'react';
import { UserCheck, UserX, Mail, Ban, User as UserIcon } from 'lucide-react';
import { Card } from '../../atoms/Card';
import { Button } from '../../atoms/Button';
import { type User } from '../../../services/usersService';
import { getUserProfileUrl, getImageUrl } from '../../../utils';

export interface UserCardProps {
  user: User;
  actionLoading: number | null;
  onActivate: (user: User) => void;
  onDeactivate: (user: User) => void;
  onSendPasswordReset: (user: User) => void;
  onShadowBanClick: (user: User) => void;
}

export const UserCard = ({
  user,
  actionLoading,
  onActivate,
  onDeactivate,
  onSendPasswordReset,
  onShadowBanClick,
}: UserCardProps) => {
  const profileUrl = getUserProfileUrl(user.username);
  const isShadowBanned = user.isShadowBanned;

  const handleCardClick = () => {
    window.open(profileUrl, '_blank');
  };

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  const avatarUrl = user.profilePhoto ? getImageUrl(user.profilePhoto) : null;

  return (
    <Card
      className={`${isShadowBanned ? 'bg-orange-50 border-orange-200' : ''} cursor-pointer hover:shadow-lg transition-shadow`}
      onClick={handleCardClick}
    >
      <div className="flex flex-col sm:flex-row items-start gap-4">
        {avatarUrl && (
          <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20">
            <img
              src={avatarUrl}
              alt={user.username}
              className="w-full h-full rounded-full object-cover border-2 border-gray-200"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}
        {!avatarUrl && (
          <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <UserIcon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0 w-full">
          <div className="mb-2">
            <h3 className="text-lg font-semibold text-gray-800 break-words">
              {user.username}
            </h3>
          </div>
          <p className="text-sm text-gray-600 mb-3 break-words">{user.email}</p>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500">
              <span className="whitespace-nowrap">ID: {user.id}</span>
              <span className="whitespace-nowrap">
                Kayıt: {new Date(user.dateJoined).toLocaleDateString('tr-TR')}
              </span>
              {user.isActive ? (
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-semibold whitespace-nowrap">
                  Aktif
                </span>
              ) : (
                <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-semibold whitespace-nowrap">
                  Pasif
                </span>
              )}
              {isShadowBanned && (
                <span className="px-2 py-1 bg-orange-200 text-orange-800 rounded font-semibold whitespace-nowrap">
                  Shadow Banned
                </span>
              )}
              {user.isSuperuser && (
                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded whitespace-nowrap">
                  Superuser
                </span>
              )}
              {user.isStaff && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded whitespace-nowrap">
                  Staff
                </span>
              )}
            </div>
            {/* Desktop buttons */}
            <div className="hidden sm:flex gap-2 flex-shrink-0 ml-auto">
              {user.isActive ? (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={(e) => handleActionClick(e, () => onDeactivate(user))}
                  disabled={actionLoading === user.id}
                  isLoading={actionLoading === user.id}
                  className="flex items-center gap-2"
                >
                  <UserX className="w-4 h-4" />
                  Pasifleştir
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={(e) => handleActionClick(e, () => onActivate(user))}
                  disabled={actionLoading === user.id}
                  isLoading={actionLoading === user.id}
                  className="flex items-center gap-2"
                >
                  <UserCheck className="w-4 h-4" />
                  Aktifleştir
                </Button>
              )}
              <Button
                variant="secondary"
                size="small"
                onClick={(e) => handleActionClick(e, () => onSendPasswordReset(user))}
                disabled={actionLoading === user.id}
                isLoading={actionLoading === user.id}
                className="flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Şifre Sıfırla
              </Button>
              <Button
                variant="secondary"
                size="small"
                onClick={(e) => handleActionClick(e, () => onShadowBanClick(user))}
                className="flex items-center gap-2"
              >
                <Ban className="w-4 h-4" />
                Shadow Ban
              </Button>
            </div>
          </div>
          {/* Mobile buttons */}
          <div className="flex flex-col sm:hidden gap-2 mt-3">
            {user.isActive ? (
              <Button
                variant="secondary"
                size="small"
                onClick={(e) => handleActionClick(e, () => onDeactivate(user))}
                disabled={actionLoading === user.id}
                isLoading={actionLoading === user.id}
                className="flex items-center justify-center gap-2 w-full"
              >
                <UserX className="w-4 h-4" />
                Pasifleştir
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="small"
                onClick={(e) => handleActionClick(e, () => onActivate(user))}
                disabled={actionLoading === user.id}
                isLoading={actionLoading === user.id}
                className="flex items-center justify-center gap-2 w-full"
              >
                <UserCheck className="w-4 h-4" />
                Aktifleştir
              </Button>
            )}
            <Button
              variant="secondary"
              size="small"
              onClick={(e) => handleActionClick(e, () => onSendPasswordReset(user))}
              disabled={actionLoading === user.id}
              isLoading={actionLoading === user.id}
              className="flex items-center justify-center gap-2 w-full"
            >
              <Mail className="w-4 h-4" />
              Şifre Sıfırlama Maili Gönder
            </Button>
            <Button
              variant="secondary"
              size="small"
              onClick={(e) => handleActionClick(e, () => onShadowBanClick(user))}
              className="flex items-center justify-center gap-2 w-full"
            >
              <Ban className="w-4 h-4" />
              Shadow Ban Tarihini Değiştir
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
